<div *ngIf="product">
    <!-- Breadcrumbs -->
    <div class="px-6 xl:px-[224px] pt-8">
        <vsf-collection-breadcrumbs [breadcrumbs]="breadcrumbs"
                                    class="mb-6"
                                    [linkLast]="true"></vsf-collection-breadcrumbs>
    </div>

    <!-- Product Main Section -->
    <div class="px-6 xl:px-[224px]">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-4">
            <!-- Left: Product Image Gallery + Action Buttons -->
            <div class="self-start">
                <vsf-asset-gallery
                    [assets]="product?.assets"
                    [selectedAssetId]="product?.featuredAsset?.id"></vsf-asset-gallery>

                <!-- Action Buttons -->
                <div class="flex gap-8 mt-8 justify-center border-gray-100">
                    <button class="flex items-center gap-2 transition-colors text-primary"
                            (click)="toggleWishlist()">
                        <svg class="w-5 h-5" [attr.fill]="inWishlist ? 'currentColor' : 'none'" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
                        </svg>
                        <span class="text-sm font-medium uppercase">{{ (inWishlist ? 'product.inWishlist' : 'product.addToWishlist') | translate }}</span>
                    </button>
                    <div class="w-[1.5px] h-[30px] bg-primary self-center"></div>
                    <button class="flex items-center gap-2 text-primary hover:text-primary transition-colors"
                            (click)="toggleCompare()">
                        <svg class="w-5 h-5" [attr.fill]="inCompare ? 'currentColor' : 'none'" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 3v18M6.75 8.25h-3A.75.75 0 0 0 3 9v7.5a.75.75 0 0 0 .75.75h3a.75.75 0 0 0 .75-.75V9a.75.75 0 0 0-.75-.75Zm13.5 0h-3a.75.75 0 0 0-.75.75v7.5a.75.75 0 0 0 .75.75h3a.75.75 0 0 0 .75-.75V9a.75.75 0 0 0-.75-.75Z"/>
                        </svg>
                        <span class="text-sm font-medium uppercase">{{ 'product.compareProduct' | translate }}</span>
                    </button>
                </div>
            </div>

            <!-- Right: Product Info -->
            <div class="flex flex-col gap-3">
                <!-- Brand / SKU -->
                <p class="text-disabled text-base" *ngIf="selectedVariant">
                    {{ selectedVariant?.sku }}
                </p>

                <!-- Product Name -->
                <h1 class="text-heading text-2xl lg:text-[28px] font-semibold leading-tight">
                    {{ product?.name }}
                </h1>

                <!-- Feature Badges -->
                <div class="grid grid-cols-3 gap-3 mt-3"
                     *ngIf="product.customFields?.warrantyText || product.customFields?.availabilityText || product.customFields?.deliveryText">
                    <div *ngIf="product.customFields?.warrantyText"
                         class="flex flex-col items-center gap-2 bg-[#FDE8D6] border border-transparent hover:border-primary focus-within:border-primary rounded-lg px-3 py-3 transition-colors">
                        <svg class="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                        </svg>
                        <span class="text-xs font-medium text-heading text-center">{{ product.customFields.warrantyText }}</span>
                    </div>
                    <div *ngIf="product.customFields?.availabilityText"
                         class="flex flex-col items-center gap-2 bg-[#FDE8D6] border border-transparent hover:border-primary focus-within:border-primary rounded-lg px-3 py-3 transition-colors">
                        <svg class="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                        </svg>
                        <span class="text-xs font-medium text-heading text-center">{{ product.customFields.availabilityText }}</span>
                    </div>
                    <div *ngIf="product.customFields?.deliveryText"
                         class="flex flex-col items-center gap-2 bg-[#FDE8D6] border border-transparent hover:border-primary focus-within:border-primary rounded-lg px-3 py-3 transition-colors">
                        <svg class="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                        </svg>
                        <span class="text-xs font-medium text-heading text-center">{{ product.customFields.deliveryText }}</span>
                    </div>
                </div>

                <!-- Installation Service -->
                <div *ngIf="product.customFields?.installationAvailable"
                     class="flex items-center justify-between bg-surface rounded-lg px-4 py-3">
                    <div class="flex items-center gap-3">
                        <svg class="w-5 h-5 text-heading" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                        </svg>
                        <span class="text-sm font-medium text-heading">{{ 'product.installationService' | translate }}</span>
                        <span *ngIf="product.customFields?.installationPrice"
                              class="text-sm font-semibold text-primary">
                            + {{ product.customFields.installationPrice | formatPrice }}
                        </span>
                    </div>
                    <button (click)="toggleInstallation()"
                            class="text-sm font-semibold px-4 py-1.5 rounded transition-colors"
                            [ngClass]="installationSelected
                                ? 'bg-primary text-white border border-primary'
                                : 'border border-primary text-primary hover:bg-primary hover:text-white'">
                        {{ installationSelected ? '✓' : '' }} {{ (installationSelected ? 'product.selected' : 'product.select') | translate }}
                    </button>
                </div>

                <!-- About This Product -->
                <div class="mt-4">
                    <h3 class="text-heading font-bold text-sm uppercase tracking-wide mb-2">{{ 'product.aboutThisProduct' | translate }}</h3>
                    <div class="text-disabled text-sm leading-relaxed"
                         [innerHTML]="product?.description | safeHtml"></div>
                </div>

            </div>
        </div>
    </div>

    <!-- Specifications -->
    <div class="px-6 xl:px-[224px] mt-10" *ngIf="specCategories.length">
        <div class="border-t border-b border-disabled pt-4 pb-4">
            <button class="flex items-center justify-between w-full" (click)="specsOpen = !specsOpen">
                <h3 class="text-heading font-semibold text-lg uppercase">{{ 'product.specifications' | translate }}</h3>
                <svg class="w-6 h-6 text-heading transition-transform" [class.rotate-180]="specsOpen" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                </svg>
            </button>
            <div *ngIf="specsOpen" class="mt-4">
                <!-- Category Tabs -->
                <div class="flex items-end">
                    <button *ngFor="let cat of specCategories; let i = index"
                            (click)="activeSpecTab = i"
                            class="px-4 pt-3 pb-2 text-base font-semibold text-heading rounded-t-lg transition-colors"
                            [class.border-l]="activeSpecTab === i"
                            [class.border-r]="activeSpecTab === i"
                            [class.border-t]="activeSpecTab === i"
                            [class.border-disabled]="activeSpecTab === i"
                            [class.border-transparent]="activeSpecTab !== i">
                        {{ cat.category }}
                    </button>
                </div>
                <!-- Spec Rows -->
                <div class="rounded-lg rounded-tl-none overflow-hidden">
                    <div *ngFor="let spec of specCategories[activeSpecTab]?.specs; let even = even"
                         class="flex justify-between items-center px-4 py-2.5 text-base text-heading"
                         [class.bg-[#f4f4f4]]="even"
                         [class.bg-[#f9f9f9]]="!even">
                        <span>{{ spec.name }}</span>
                        <span>{{ spec.value }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Variants Table: SELECT ITEM -->
    <div class="min-w-0 px-6 xl:px-[224px] mt-20 pb-20" *ngIf="product?.variants?.length >= 1">
        <h2 class="text-heading text-lg font-semibold pb-4 uppercase">{{ 'product.selectItem' | translate }}</h2>

        <div class="overflow-hidden rounded-lg border border-[#F48C34]/30">
            <!-- Shared filter row (single ngModel per field — works on all breakpoints) -->
            <div class="flex flex-col gap-3 border-b-[3px] border-[#F48C34] bg-[#FDE8D6] p-4 sm:flex-row sm:flex-wrap sm:items-end sm:gap-x-4 sm:gap-y-3 lg:flex-nowrap lg:gap-4">
                <div class="hidden w-[100px] shrink-0 text-base font-semibold text-heading lg:block">{{ 'product.sku' | translate }}</div>
                <ng-container *ngFor="let group of optionGroups">
                    <div class="min-w-0 flex-1 sm:min-w-[140px] lg:max-w-[200px]">
                        <span class="mb-1 block text-sm font-semibold text-heading lg:text-base">{{ group.name }}</span>
                        <select
                            class="h-9 w-full max-w-full rounded border border-[#a1a4aa] bg-white px-2 text-sm appearance-none focus:border-[#F48C34] focus:outline-none lg:h-8 lg:min-w-[4.5rem] lg:max-w-[6.5rem] lg:px-1"
                            [ngModel]="variantFilters[group.id] || ''"
                            (ngModelChange)="variantFilters[group.id] = $event">
                            <option value=""></option>
                            <option *ngFor="let val of getUniqueOptionValues(group.id)" [value]="val">{{ val }}</option>
                        </select>
                    </div>
                </ng-container>
                <div class="hidden w-[112px] shrink-0 lg:block" aria-hidden="true"></div>
            </div>

            <div *ngIf="filteredVariants.length === 0"
                 class="border-t border-gray-200 bg-[#f9f9f9] px-6 py-10 text-center text-base text-heading"
                 role="status">
                {{ 'product.noVariantsMatchFilter' | translate }}
            </div>

            <!-- Desktop / large tablet: table rows -->
            <div class="hidden lg:block" *ngIf="filteredVariants.length > 0">
                <div *ngFor="let variant of filteredVariants; let odd = odd; let last = last"
                     class="flex min-w-0 items-center gap-4 border-b border-gray-200 py-4 ps-6 pe-4 last:border-b-0"
                     [class.bg-[#f4f4f4]]="!odd"
                     [class.bg-[#f9f9f9]]="odd">
                    <div class="w-[100px] shrink-0 text-base font-medium text-heading underline">{{ variant.sku }}</div>
                    <ng-container *ngFor="let group of optionGroups">
                        <div class="min-w-0 flex-1 truncate text-base text-heading">{{ getOptionValue(variant, group.id) }}</div>
                    </ng-container>
                    <div class="sticky end-0 z-[1] flex w-[120px] shrink-0 justify-end border-s border-gray-200/80 ps-3"
                         [class.bg-[#f4f4f4]]="!odd"
                         [class.bg-[#f9f9f9]]="odd">
                        <ng-container *ngTemplateOutlet="variantRowActions; context: { $implicit: variant }"></ng-container>
                    </div>
                </div>
            </div>

            <!-- Mobile / small tablet: stacked cards, full-width cart -->
            <div class="lg:hidden" *ngIf="filteredVariants.length > 0">
                <div *ngFor="let variant of filteredVariants; let odd = odd; let last = last"
                     class="border-b border-gray-200 p-4 last:border-b-0"
                     [class.bg-[#f4f4f4]]="!odd"
                     [class.bg-[#f9f9f9]]="odd">
                    <div class="mb-3 text-sm font-semibold text-heading">
                        <span class="text-disabled">{{ 'product.sku' | translate }}:</span>
                        <span class="ms-1 underline">{{ variant.sku }}</span>
                    </div>
                    <dl class="mb-4 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                        <ng-container *ngFor="let group of optionGroups">
                            <div class="min-w-0 flex flex-col gap-0.5">
                                <dt class="text-xs font-medium text-disabled">{{ group.name }}</dt>
                                <dd class="text-base font-medium text-heading">{{ getOptionValue(variant, group.id) }}</dd>
                            </div>
                        </ng-container>
                    </dl>
                    <div class="flex w-full">
                        <ng-container *ngTemplateOutlet="variantRowActions; context: { $implicit: variant }"></ng-container>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <ng-template #variantRowActions let-variant>
        <button
            *ngIf="!qtyInCart[variant.id] && !isOutOfStock(variant)"
            (click)="openAddToCartDialog(variant)"
            class="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#F48C34] px-6 py-3 text-white transition-colors hover:bg-[#e07a25] lg:inline-flex"
            [class.opacity-50]="inFlightVariantId === variant.id"
            [disabled]="inFlightVariantId === variant.id">
            <svg class="h-6 w-6 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z"/>
            </svg>
            <span class="text-sm font-semibold uppercase lg:hidden">{{ 'product.addToCart' | translate }}</span>
        </button>
        <button
            *ngIf="!qtyInCart[variant.id] && isOutOfStock(variant)"
            type="button"
            disabled
            class="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-disabled px-6 py-3 text-white shadow-none cursor-not-allowed lg:inline-flex">
            <span class="text-xs font-semibold uppercase whitespace-nowrap">{{ 'product.outOfStock' | translate }}</span>
        </button>
        <div *ngIf="qtyInCart[variant.id]" class="inline-flex w-full max-w-xs items-center justify-center rounded-lg border border-[#F48C34] overflow-hidden lg:w-auto lg:max-w-none">
            <button
                (click)="adjustQuantity(variant, -1)"
                class="flex h-10 w-10 shrink-0 items-center justify-center bg-[#F48C34] text-lg font-bold text-white transition-colors hover:bg-[#e07a25]"
                [disabled]="inFlightVariantId === variant.id">
                &minus;
            </button>
            <span class="flex h-10 w-12 items-center justify-center bg-white text-base font-semibold text-heading">
                {{ qtyInCart[variant.id] }}
            </span>
            <button
                (click)="adjustQuantity(variant, 1)"
                class="flex h-10 w-10 shrink-0 items-center justify-center bg-[#F48C34] text-lg font-bold text-white transition-colors hover:bg-[#e07a25]"
                [disabled]="inFlightVariantId === variant.id">
                +
            </button>
        </div>
    </ng-template>


    <!-- Related Products -->
    <ng-container *ngIf="relatedProducts$ | async as relatedProducts">
        <section class="px-6 xl:px-[224px] py-20" *ngIf="relatedProducts.length > 0">
            <h2 class="text-heading text-[32px] leading-[40px] font-semibold mb-6">{{ 'product.relatedProducts' | translate }}</h2>
            <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <vsf-product-card *ngFor="let product of relatedProducts; trackBy: trackByProductId"
                                  [product]="product">
                </vsf-product-card>
            </div>
        </section>
    </ng-container>
</div>

<!-- Loading Skeleton -->
<div class="px-6 xl:px-[224px] py-8" *ngIf="!product">
    <!-- Breadcrumb skeleton -->
    <div class="h-4 w-64 bg-gray-200 animate-pulse rounded mb-6"></div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-4">
        <!-- Image skeleton -->
        <div>
            <div class="bg-gray-200 animate-pulse rounded-lg aspect-square"></div>
            <div class="flex gap-2 mt-2">
                <div *ngFor="let i of [1,2,3]" class="h-24 w-24 bg-gray-200 animate-pulse rounded-lg"></div>
            </div>
            <div class="flex gap-8 mt-8 pt-6 border-t border-gray-100">
                <div class="h-5 w-36 bg-gray-200 animate-pulse rounded"></div>
                <div class="h-5 w-44 bg-gray-200 animate-pulse rounded"></div>
            </div>
        </div>
        <!-- Info skeleton -->
        <div class="space-y-4">
            <div class="h-4 w-20 bg-gray-200 animate-pulse rounded"></div>
            <div class="h-8 w-72 bg-gray-200 animate-pulse rounded"></div>
            <div class="h-7 w-32 bg-gray-200 animate-pulse rounded"></div>
            <div class="grid grid-cols-3 gap-3 mt-3">
                <div class="h-20 bg-gray-200 animate-pulse rounded-lg"></div>
                <div class="h-20 bg-gray-200 animate-pulse rounded-lg"></div>
                <div class="h-20 bg-gray-200 animate-pulse rounded-lg"></div>
            </div>
            <div class="h-12 bg-gray-200 animate-pulse rounded-lg"></div>
            <div class="space-y-2 mt-4">
                <div class="h-5 w-40 bg-gray-200 animate-pulse rounded"></div>
                <div class="h-4 w-full bg-gray-200 animate-pulse rounded"></div>
                <div class="h-4 w-3/4 bg-gray-200 animate-pulse rounded"></div>
            </div>
            <div class="h-px bg-gray-200 mt-4"></div>
            <div class="h-5 w-32 bg-gray-200 animate-pulse rounded"></div>
        </div>
    </div>

    <!-- Related products skeleton -->
    <div class="mt-20">
        <div class="h-8 w-80 bg-gray-200 animate-pulse rounded mb-6"></div>
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div *ngFor="let i of [1,2,3,4]" class="flex flex-col gap-2 animate-pulse">
                <div class="bg-gray-200 rounded h-[350px] lg:h-[482px]"></div>
                <div class="h-4 bg-gray-200 rounded w-1/3"></div>
                <div class="h-5 bg-gray-200 rounded w-2/3"></div>
                <div class="h-5 bg-gray-200 rounded w-1/4"></div>
            </div>
        </div>
    </div>
</div>

<ng-template #addedToCartTemplate let-variant="variant" let-quantity="quantity" let-close="closeFn">
    <div class="flex">
        <div class="mr-8">
            <img
                class="rounded"
                [src]="(variant.featuredAsset?.preview || product?.featuredAsset?.preview) + '?preset=tiny'"
                alt="product thumbnail"
            />
        </div>
        <div class="text-sm">{{ quantity }} x {{ variant.name }}</div>
    </div>
    <div class="flex justify-end">
        <button type="button"
                (click)="viewCartFromNotification(close)"
                class="inline-flex items-center rounded border border-transparent bg-primary-100 px-2.5 py-1.5 text-xs font-medium text-primary-700 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2">
            View cart
        </button>
    </div>
</ng-template>
